import { ArrowUpRight, Circle, Hand, PenLine, Square, Trash2, Type, Undo2 } from "lucide-react";
import { useRef, useState } from "react";

import type { Shape } from "@/api/client";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { addPoint, shapeFrom } from "@/viewer/annotationGeom";
import type { Point, Tool } from "@/viewer/annotationGeom";
import { ShapeView } from "@/viewer/shapes";
import type { ViewerGeometry } from "@/viewer/TypeViewer";

export type Mode = "pan" | Tool;

/** Mark colours. Stored as hex on the shape, so they print the same anywhere. */
export const MARK_COLOURS = ["#d03030", "#2f6fde", "#d98a00", "#1f9d55"] as const;

interface LayerProps {
  geometry: ViewerGeometry;
  /** The marks on the sheet being shown. */
  shapes: readonly Shape[];
  pageId: string;
  mode: Mode;
  colour: string;
  /** What the text tool places. */
  text: string;
  selected: number | null;
  onSelect: (index: number | null) => void;
  onDraw: (shape: Shape) => void;
}

/**
 * The estimator's marks over the sheet, inside the viewer's SVG so they share its page
 * coordinates. With a drawing tool the layer takes the pointer; with the hand, marks
 * can be picked to delete and the viewer pans.
 */
export function AnnotationLayer({
  geometry,
  shapes,
  pageId,
  mode,
  colour,
  text,
  selected,
  onSelect,
  onDraw,
}: LayerProps) {
  const [draft, setDraft] = useState<Point[] | null>(null);
  const drawing = useRef(false);
  const { page, viewWidth, toPage } = geometry;
  const unit = viewWidth / page.w;
  const tool = mode === "pan" ? null : mode;

  const point = (event: React.PointerEvent) => toPage(event.clientX, event.clientY);

  const finish = (points: Point[] | null) => {
    drawing.current = false;
    setDraft(null);
    if (!tool || !points) {
      return;
    }
    const shape = shapeFrom(tool, points, { colour, pageId, minSpan: unit / 100, text });
    if (shape) {
      onDraw(shape);
    }
  };

  return (
    <g data-annotations>
      {shapes.map((shape, i) => (
        <g
          key={i}
          data-mark={i}
          className={tool ? undefined : "cursor-pointer"}
          onPointerDown={(event) => {
            if (!tool) {
              event.stopPropagation();
              onSelect(i === selected ? null : i);
            }
          }}
        >
          <ShapeView shape={shape} page={page} viewWidth={viewWidth} selected={i === selected} />
          {tool ? null : (
            <ShapeView shape={shape} page={page} viewWidth={viewWidth} hitWidth={16} />
          )}
        </g>
      ))}
      {tool ? (
        <rect
          data-draw-surface
          x={0}
          y={0}
          width={page.w}
          height={page.h}
          fill="transparent"
          style={{ cursor: "crosshair" }}
          onPointerDown={(event) => {
            const p = point(event);
            if (!p || event.button !== 0) {
              return;
            }
            event.stopPropagation();
            event.currentTarget.setPointerCapture?.(event.pointerId);
            const start = addPoint(tool, [], p, 0);
            if (tool === "text") {
              finish(start);
              return;
            }
            drawing.current = true;
            setDraft(start);
          }}
          onPointerMove={(event) => {
            const p = point(event);
            if (!drawing.current || !p) {
              return;
            }
            setDraft((d) => (d ? addPoint(tool, d, p, unit / 300) : d));
          }}
          onPointerUp={() => finish(draft)}
          onPointerCancel={() => {
            drawing.current = false;
            setDraft(null);
          }}
        />
      ) : null}
      {tool && draft && draft.length > 1 ? (
        <ShapeView
          shape={{ kind: tool, page_id: pageId, points: draft, colour, text: null }}
          page={page}
          viewWidth={viewWidth}
        />
      ) : null}
    </g>
  );
}

const TOOLS: readonly { mode: Mode; label: string; icon: typeof Hand }[] = [
  { mode: "pan", label: "Move and select", icon: Hand },
  { mode: "arrow", label: "Arrow", icon: ArrowUpRight },
  { mode: "rect", label: "Box", icon: Square },
  { mode: "ellipse", label: "Ellipse", icon: Circle },
  { mode: "freehand", label: "Pen", icon: PenLine },
  { mode: "text", label: "Text", icon: Type },
];

interface ToolbarProps {
  mode: Mode;
  onMode: (mode: Mode) => void;
  colour: string;
  onColour: (colour: string) => void;
  text: string;
  onText: (text: string) => void;
  canUndo: boolean;
  onUndo: () => void;
  canDelete: boolean;
  onDelete: () => void;
}

/** The drawing tools, over the top-left of the viewer. */
export function AnnotationToolbar({
  mode,
  onMode,
  colour,
  onColour,
  text,
  onText,
  canUndo,
  onUndo,
  canDelete,
  onDelete,
}: ToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-label="Markup"
      className="flex flex-wrap items-center gap-1 rounded-lg border border-line bg-surface/95 p-1 shadow-card"
    >
      {TOOLS.map(({ mode: m, label, icon: Icon }) => (
        <Button
          key={m}
          size="sm"
          variant={mode === m ? "primary" : "ghost"}
          className="w-8 px-0"
          aria-label={label}
          aria-pressed={mode === m}
          title={label}
          onClick={() => onMode(m)}
        >
          <Icon aria-hidden="true" />
        </Button>
      ))}
      {mode === "text" ? (
        <input
          type="text"
          aria-label="Text to place"
          value={text}
          placeholder="Text, then click the sheet"
          onChange={(event) => onText(event.target.value)}
          className="h-8 w-48 rounded-md border border-line-strong bg-surface px-2 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none"
        />
      ) : null}
      <span aria-hidden="true" className="mx-1 h-5 w-px bg-line-strong" />
      <div role="radiogroup" aria-label="Colour" className="flex gap-1 px-1">
        {MARK_COLOURS.map((c) => (
          <button
            key={c}
            type="button"
            role="radio"
            aria-checked={colour === c}
            aria-label={`Colour ${c}`}
            onClick={() => onColour(c)}
            className={cn(
              "size-5 rounded-full ring-offset-1 ring-offset-surface",
              colour === c ? "ring-2 ring-ink" : "ring-1 ring-black/10",
            )}
            style={{ background: c }}
          />
        ))}
      </div>
      <span aria-hidden="true" className="mx-1 h-5 w-px bg-line-strong" />
      <Button
        size="sm"
        variant="ghost"
        className="w-8 px-0"
        aria-label="Undo"
        title="Undo"
        disabled={!canUndo}
        onClick={onUndo}
      >
        <Undo2 aria-hidden="true" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="w-8 px-0"
        aria-label="Delete mark"
        title="Delete mark"
        disabled={!canDelete}
        onClick={onDelete}
      >
        <Trash2 aria-hidden="true" />
      </Button>
    </div>
  );
}
